import React from 'react';
import { useNavigate } from 'react-router-dom';

interface RoleCard {
  key: string;
  icon: string;
  title: string;
  subtitle: string;
  color: string;
  tasks: string[];
  route: string;
}

const ROLES: RoleCard[] = [
  {
    key: 'coordinator',
    icon: '\uD83D\uDCCB',
    title: 'Site Coordinator',
    subtitle: 'Enter and manage visit data',
    color: '#38b2ac',
    tasks: [
      'Enter today\u2019s visit data',
      'Track patient consent status',
      'Log adverse events',
      'Monitor recording delays',
    ],
    route: '/home/coordinator',
  },
  {
    key: 'monitor',
    icon: '\uD83D\uDD0D',
    title: 'Clinical Monitor',
    subtitle: 'Verify and audit site data',
    color: '#4299e1',
    tasks: [
      'Review SDV completion',
      'Raise and resolve queries',
      'Check protocol compliance',
      'Generate monitor report',
    ],
    route: '/home/monitor',
  },
  {
    key: 'budget',
    icon: '\uD83D\uDCB2',
    title: 'Budget Coordinator',
    subtitle: 'Protocol budgeting and tracking',
    color: '#9f7aea',
    tasks: [
      'Review protocol SOA grid',
      'Map procedures to CPT codes',
      'Calculate per-patient costs',
      'Track budget variance',
    ],
    route: '/home/budget',
  },
];

export default function RoleSelect() {
  const navigate = useNavigate();

  const handleSelect = (role: RoleCard) => {
    localStorage.setItem('d3ri_selected_role', role.key);
    navigate(role.route);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.logo}>D3RI</div>
        <div style={styles.tagline}>Clinical Trial Decision Intelligence</div>
      </div>

      <h1 style={styles.title}>Select your role</h1>
      <p style={styles.subtitle}>Choose your workflow to get started</p>

      <div style={styles.grid}>
        {ROLES.map((role) => (
          <button
            key={role.key}
            style={{ ...styles.card, borderColor: role.color }}
            onClick={() => handleSelect(role)}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-4px)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 8px 24px ${role.color}22`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            <div style={styles.cardIcon}>{role.icon}</div>
            <div style={{ ...styles.cardTitle, color: role.color }}>{role.title}</div>
            <div style={styles.cardSubtitle}>{role.subtitle}</div>
            <ul style={styles.taskList}>
              {role.tasks.map((task, i) => (
                <li key={i} style={styles.taskItem}>
                  <span style={{ ...styles.taskDot, backgroundColor: role.color }} />
                  {task}
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>

      <p style={styles.hint}>Not sure? Start with Site Coordinator</p>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0d1117',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    textAlign: 'center',
    marginBottom: 32,
  },
  logo: {
    fontSize: 48,
    fontWeight: 800,
    color: '#63b3ed',
    letterSpacing: 4,
  },
  tagline: {
    fontSize: 14,
    color: '#718096',
    marginTop: 4,
    letterSpacing: 1,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#e2e8f0',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: '#a0aec0',
    marginBottom: 40,
  },
  grid: {
    display: 'flex',
    gap: 24,
    maxWidth: 960,
    width: '100%',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },
  card: {
    flex: '1 1 280px',
    maxWidth: 300,
    backgroundColor: '#1a202c',
    border: '2px solid',
    borderRadius: 12,
    padding: '28px 24px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit',
  },
  cardIcon: {
    fontSize: 36,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 13,
    color: '#a0aec0',
    marginBottom: 20,
  },
  taskList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  taskItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#cbd5e0',
  },
  taskDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  hint: {
    marginTop: 40,
    fontSize: 13,
    color: '#4a5568',
    fontStyle: 'italic',
  },
};
